import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Wrench, CheckCircle } from 'lucide-react';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Introdu adresa de email');
      return;
    }

    try {
      setLoading(true);
      await api.post('/auth/forgot-password', { email });
      setSent(true);
      toast.success('Email trimis cu succes!');
    } catch (error) {
      // Don't reveal if email exists or not for security
      toast.error(error.response?.data?.detail || 'Daca adresa exista in sistem, vei primi un email cu instructiuni');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-blue-100 p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Verifica email-ul</h2>
            <p className="text-slate-600 mb-6">
              Daca adresa <strong>{email}</strong> exista in sistem, vei primi un email cu instructiuni pentru resetarea parolei.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              Verifica si folderul Spam daca nu gasesti email-ul in Inbox.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="w-full btn-primary h-12"
            >
              Inapoi la Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-blue-100 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center pb-2">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-[#0052CC] rounded-xl flex items-center justify-center">
              <Wrench className="w-7 h-7 text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-bold text-[#0052CC] tracking-tight">MAINTIX</h1>
              <p className="text-[10px] text-slate-500 -mt-0.5">Smart Maintenance & Asset Control</p>
            </div>
          </div>
          <CardTitle className="text-xl">Recuperare Parola</CardTitle>
          <CardDescription>
            Introdu adresa de email asociata contului tau
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Adresa de email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="exemplu@companie.ro"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 pl-10 text-base"
                  autoComplete="email"
                  required
                  data-testid="forgot-email"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary h-12"
              disabled={loading}
              data-testid="forgot-submit"
            >
              {loading ? 'SE TRIMITE...' : 'TRIMITE LINK RESETARE'}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-slate-200 text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center gap-2 text-sm text-[#0052CC] hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Inapoi la Login
            </Link>
          </div>

          <div className="mt-4 p-3 bg-amber-50 rounded-lg text-center">
            <p className="text-xs text-amber-700">
              <strong>Nota:</strong> Daca nu ai acces la email, contacteaza administratorul sistemului pentru resetarea manuala a parolei.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
